import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import {
  checkPassword as actionCheckPassword,
  fetchAuth as fetchAuthAction,
  fetchSsoInitialPassword,
} from '../actions/auth';
import actionChangeLang from '../actions/i18n';
import { updateMarketing as actionUpdateMarketing } from '../actions/user';
import { generateUpdatedMarketing } from '../actions/utils';

import UserNav from './UserNav';
import RtsUserNav from './RtsUserNav';

import Loader from './utils/Loader';

import '../styles/main.scss';
import ChangePassword from './gui/ChangePassword';
import SsoPasswordModal from './gui/SsoPasswordModal';

const isRtsEditorPath = pathname =>
  /\/i\/gui\/(snap|code)(\/|$)/.test(pathname);

class Main extends React.PureComponent {
  constructor(...args) {
    super(...args);

    this.handleStorage = this.handleStorage.bind(this);
    this.close = this.close.bind(this);
    this.state = {
      showPasswordModal: false,
      showSsoPasswordModal: false,
      ssoPassword: '',
      ssoEmail: '',
      passwordChecked: false,
      appIsMounted: false,
    };
  }

  componentDidCatch(error, info) {
    console.log('Error occured', error, info);
  }

  loadSsoPassword() {
    if (this._ssoPasswordFetched) {
      return;
    }
    this._ssoPasswordFetched = true;
    fetchSsoInitialPassword()
      .then(payload => {
        if (payload && payload.password) {
          this.setState({
            showSsoPasswordModal: true,
            ssoPassword: payload.password,
            ssoEmail: payload.email || '',
          });
        }
      })
      .catch(() => {});
  }

  componentWillReceiveProps(newProps) {
    const { user } = newProps;
    if (user) {
      this.loadSsoPassword();
    }
    if (!this.state.passwordChecked && user) {
      this.props.updateMarketing(generateUpdatedMarketing(user));

      if (newProps.hasPassword === undefined) {
        newProps.checkPassword();
      } else {
        this.setState({
          showPasswordModal: !newProps.hasPassword,
          passwordChecked: true,
        });
      }
    }
  }

  componentDidMount() {
    window.addEventListener('storage', this.handleStorage);
    // Always re-check session on refresh (cookie connect.sid), not only when localStorage cache exists
    this.props.fetchAuth();
    if (this.props.user) {
      this.loadSsoPassword();
    }
    //We render this component only at the ClientSide
    requestAnimationFrame(() => {
      this.setState({ appIsMounted: true });
    });
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.handleStorage);
  }

  close() {
    this.setState({ showPasswordModal: false });
  }

  closeSsoPassword = () => {
    this.setState({
      showSsoPasswordModal: false,
      ssoPassword: '',
    });
  };

  handleStorage(e) {
    const { updateLang } = this.props;
    if (e.key === 'lang') updateLang(e.newValue);
    // TODO What is this
    if (e.key === 'shouldUpdate' && e.newValue) {
      const { location } = window;
      window.location = location;
    }
  }

  render() {
    const { children, loader, loaderTimeout, user, pathname } = this.props;
    const {
      showPasswordModal,
      showSsoPasswordModal,
      ssoPassword,
      ssoEmail,
      passwordChecked,
    } = this.state;
    const isAuthPage =
      pathname.includes('/i/auth') ||
      pathname.includes('/i/reset') ||
      pathname.includes('/i/register') ||
      pathname.includes('/i/confirm');
    const isEditorRts = isRtsEditorPath(pathname);
    const isRtsLayout =
      !pathname.includes('/all-old') &&
      !isEditorRts &&
      (/\/i\/(all|snap|code|plain|tinkercad|settings|profile)(\/|$)/.test(
        pathname
      ) ||
        /\/i\/gui(\/|$)/.test(pathname) ||
        /\/newall(\/|$)/.test(pathname));
    const useRtsNav = isRtsLayout || isEditorRts;
    //const showNicknameModal = user && !user.name;
    return (
      <div>
        {this.state.appIsMounted && (
          <div
            className={`page-wrap container-fluid${
              isAuthPage ? ' page-wrap--auth' : ''
            }${isRtsLayout ? ' page-wrap--rts' : ''}${
              isEditorRts ? ' page-wrap--rts-editor' : ''
            }`}
          >
            {!isAuthPage &&
              (useRtsNav ? (
                <RtsUserNav pathname={pathname} />
              ) : (
                <UserNav pathname={pathname} />
              ))}

            {/* <HelpLink /> */}

            <main
              className={
                isEditorRts
                  ? 'main-rts-editor'
                  : isRtsLayout
                    ? 'main-rts'
                    : 'row'
              }
            >
              {children}

              {user && passwordChecked && (
                <ChangePassword show={showPasswordModal} close={this.close} />
              )}
              <SsoPasswordModal
                show={showSsoPasswordModal}
                close={this.closeSsoPassword}
                email={ssoEmail}
                password={ssoPassword}
              />
            </main>

            {/* Type Safety */}
            <Loader
              data={loader instanceof Map ? loader : undefined}
              loaderTimeout={loaderTimeout}
            />
          </div>
        )}
      </div>
    );
  }
}

Main.propTypes = {
  children: PropTypes.any.isRequired,
  loader: PropTypes.any.isRequired,
  updateLang: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  loader: state.utils.loader,
  loaderTimeout: state.utils.loaderTimeout,
  user: state.auth.user,
  isAuthCached: state.auth.cached,
  hasPassword: state.auth.hasPassword,
  pathname: ownProps.location.pathname,
  locale: state.i18n.locale,
});

const mapDispatchToProps = dispatch => ({
  fetchAuth: () => {
    dispatch(fetchAuthAction());
  },
  updateMarketing: obj => {
    dispatch(actionUpdateMarketing(obj));
  },
  updateLang: lang => {
    dispatch(actionChangeLang(lang));
  },
  checkPassword: () => {
    dispatch(actionCheckPassword());
  },
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Main);
